import { lazy, Suspense, useCallback, useState } from 'react'

import { clsx } from 'clsx'

import { T } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { SystemSpaceSlug, useSpaceUtil } from '@tribeplatform/react-sdk/hooks'
import { Popover } from '@tribeplatform/react-ui-kit/Popover'
import { TopBar } from '@tribeplatform/react-ui-kit/TopBar'

import { NotificationSettingsLink } from '../../Notification/controls/NotificationSettingsLink.js'
import { NotificationsMarkAsRead } from '../../Notification/controls/NotificationsMarkAsRead.js'
import { NotificationDropdownLoading } from '../../Notification/NotificationDropdownLoading.js'
import { NavbarControlItem } from '../constants.js'
import { NotificationIndicatorBadge } from '../NotificationIndicatorBadge.js'
import { getIcon } from '../utils.js'

export type NavbarControlNotificationProps = {
  item: NavbarControlItem
  className?: string
}

const NotificationDropdownList = lazy(() =>
  import('../../Notification/NotificationDropdownList.js').then(m => ({
    default: m.NotificationDropdownList,
  })),
)

export const NavbarControlNotification = ({
  item,
  className,
}: NavbarControlNotificationProps) => {
  const { Link } = useRouter()

  const [popoverActive, setPopoverActive] = useState(false)

  const close = () => setPopoverActive(false)
  const toggle = useCallback(
    () => setPopoverActive(popoverActive => !popoverActive),
    [],
  )

  const { generatePath } = useSpaceUtil()

  const Icon = getIcon(item.icon)

  return (
    <Popover
      placement="bottom-end"
      open={popoverActive}
      onClose={close}
      activator={
        <NotificationIndicatorBadge className={clsx('translate-y-0')}>
          <TopBar.Button
            rounded
            onClick={toggle}
            icon={Icon ? <Icon /> : null}
            aria-label={item.label}
            className={className}
          />
        </NotificationIndicatorBadge>
      }
      fluidContent={false}
      fixed
    >
      <div className="flex flex-col">
        <div className="border-b border-line-subdued font-bold px-3 py-2 flex items-center gap-1">
          <Link
            className="flex-grow text-content-subdued hover:text-content-hovered"
            href={generatePath(SystemSpaceSlug.NOTIFICATIONS)}
            onClick={close}
          >
            <T defaultMessage="Notifications" id="Generics.Notifications" />
          </Link>
          <NotificationsMarkAsRead />
          <NotificationSettingsLink onClick={close} />
        </div>

        <div className="grow">
          <Suspense fallback={<NotificationDropdownLoading />}>
            <NotificationDropdownList closeDropdown={close} />
          </Suspense>
        </div>
      </div>
    </Popover>
  )
}
